import {
  ArrowRight,
  CalendarClock,
  Check,
  ShieldCheck,
  UsersRound,
  WalletCards,
} from "lucide-react";
import { Link, type MetaFunction } from "react-router";

import { Button } from "@/ui/button";

import { PricingPlans } from "./pricing-plans";

export const meta: MetaFunction = () => {
  return [
    { title: `Pricing | ${publicEnv.siteTitle}` },
    {
      name: "description",
      content:
        "Choose simple monthly or annual household pricing for Cents FYI, with every family ledger feature included.",
    },
  ];
};

// Explain the household subscription, compare billing frequencies, and answer
// the practical questions families have before starting.
export default function Pricing() {
  const trialDays = publicEnv.stripeTrialDays;

  return (
    <>
      <section className="grid items-end gap-10 py-18 sm:py-24 lg:grid-cols-[1fr_20rem] lg:py-28">
        <div className="max-w-3xl">
          <p className="text-primary font-mono text-xs font-semibold tracking-[0.16em] uppercase">
            Simple family pricing
          </p>
          <h1 className="mt-5 font-serif text-5xl leading-[0.98] font-semibold tracking-[-0.035em] sm:text-6xl">
            One household.
            <br />
            Every feature.
          </h1>
          <p className="text-muted-foreground mt-6 max-w-2xl text-lg leading-8">
            Choose monthly flexibility or annual value. Either way, one
            subscription covers the family ledger, every guardian, and every
            child—without connecting a bank account.
          </p>
          <Button asChild className="mt-8 h-12 px-6 text-base" size="lg">
            <a href="#plans">
              Compare plans
              <ArrowRight />
            </a>
          </Button>
        </div>

        <aside className="bg-secondary/70 rounded-2xl border p-6">
          <CalendarClock className="text-primary size-5" />
          <p className="mt-4 font-serif text-2xl font-semibold">
            {trialDays > 0 ? `${trialDays} days free` : "Start when ready"}
          </p>
          <p className="text-muted-foreground mt-2 text-sm leading-6">
            {trialDays > 0
              ? "Try the complete family ledger before your first payment. No card is required to begin."
              : "Pick the billing schedule that works for your household and manage it from Billing."}
          </p>
        </aside>
      </section>

      <section
        aria-labelledby="plans-title"
        className="scroll-mt-20 pb-24 sm:pb-32"
        id="plans"
      >
        <div className="grid items-end gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="text-primary font-mono text-xs font-semibold tracking-[0.16em] uppercase">
              Choose your rhythm
            </p>
            <h2
              className="mt-4 font-serif text-4xl leading-tight font-semibold sm:text-5xl"
              id="plans-title"
            >
              Monthly or annual.
            </h2>
          </div>
          <p className="text-muted-foreground max-w-xl leading-7 lg:justify-self-end">
            Both options unlock the same product. Annual billing offers the best
            current value; monthly billing keeps the commitment smaller.
          </p>
        </div>

        <PricingPlans className="mt-12" />
        <p className="text-muted-foreground mt-4 text-center text-xs">
          Prices are in USD. Payments are securely handled by Stripe.
        </p>
      </section>

      <section className="bg-primary text-primary-foreground overflow-hidden rounded-3xl px-6 py-12 sm:px-10 sm:py-16 lg:px-16">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <p className="font-mono text-xs tracking-[0.16em] uppercase opacity-70">
              Included in both plans
            </p>
            <h2 className="mt-4 font-serif text-4xl leading-tight font-semibold sm:text-5xl">
              Everything a family needs to keep the story straight.
            </h2>
          </div>

          <div className="grid gap-x-8 gap-y-7 sm:grid-cols-2">
            <IncludedFeature icon={WalletCards} title="One shared ledger">
              Track what came in, what went out, and the balance that remains.
            </IncludedFeature>
            <IncludedFeature icon={UsersRound} title="The whole household">
              Give guardians shared control and children a clear, focused view.
            </IncludedFeature>
            <IncludedFeature icon={CalendarClock} title="Recurring allowance">
              Schedule regular additions without rebuilding the same entry.
            </IncludedFeature>
            <IncludedFeature icon={ShieldCheck} title="History you can trust">
              Edit details or replace and reverse entries without silently
              erasing their history.
            </IncludedFeature>
          </div>
        </div>
      </section>

      <section className="grid gap-12 py-24 sm:py-32 lg:grid-cols-[0.75fr_1.25fr]">
        <div>
          <p className="text-primary font-mono text-xs font-semibold tracking-[0.16em] uppercase">
            Pricing FAQ
          </p>
          <h2 className="mt-4 font-serif text-4xl leading-tight font-semibold sm:text-5xl">
            The practical details.
          </h2>
        </div>

        <div className="divide-y border-y">
          <PricingQuestion question="Does one subscription cover every child?">
            Yes. Billing belongs to the household, so the plan includes its
            guardians and child ledgers rather than charging per child.
          </PricingQuestion>
          <PricingQuestion question="Do I need to connect a bank account?">
            No. {publicEnv.siteTitle} is a family record, not a bank. It does
            not hold or move the money shown in the ledger.
          </PricingQuestion>
          {trialDays > 0 && (
            <PricingQuestion question="Do I need a card for the free trial?">
              No. You can start the {trialDays}-day trial without a payment
              method. Add one in Billing before the trial ends to continue
              without interruption.
            </PricingQuestion>
          )}
          <PricingQuestion question="Can I change plans or cancel?">
            Yes. A guardian can switch between the available monthly and annual
            options, update the payment method, or manage cancellation from
            Billing.
          </PricingQuestion>
        </div>
      </section>

      <section className="bg-secondary/70 mb-24 rounded-3xl px-6 py-14 text-center sm:px-10 sm:py-18">
        <p className="font-mono text-xs font-semibold tracking-[0.16em] uppercase">
          One family, one calm record
        </p>
        <h2 className="mx-auto mt-4 font-serif text-4xl font-semibold sm:text-5xl lg:whitespace-nowrap">
          Start making sense of every cent.
        </h2>
        <Button asChild className="mt-8 h-12 px-6 text-base" size="lg">
          <Link to="/login?redirectTo=%2Fbilling">
            {trialDays > 0
              ? `Start your free ${trialDays}-day trial`
              : "Create a family ledger"}
            <ArrowRight />
          </Link>
        </Button>
      </section>
    </>
  );
}

interface IncludedFeatureProps {
  children: string;
  icon: typeof Check;
  title: string;
}

// Summarize one product capability included at every billing frequency.
function IncludedFeature({
  children,
  icon: Icon,
  title,
}: IncludedFeatureProps) {
  return (
    <article>
      <Icon className="size-5 opacity-80" />
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-6 opacity-75">{children}</p>
    </article>
  );
}

interface PricingQuestionProps {
  children: string;
  question: string;
}

// Reveal pricing details on demand while keeping the page easy to scan.
function PricingQuestion({ children, question }: PricingQuestionProps) {
  return (
    <details className="group py-5">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium">
        {question}
        <span className="text-primary text-xl transition-transform group-open:rotate-45">
          +
        </span>
      </summary>
      <p className="text-muted-foreground max-w-2xl pt-3 text-sm leading-7">
        {children}
      </p>
    </details>
  );
}
